import React from "react";
import healthCareImg from "../../images/use-case/health-care-img.png";
import governmentImg from "../../images/use-case/government-img.png";
import financeImg from "../../images/use-case/finance-img.png";
import telecomImg from "../../images/use-case/telocom-img.png";
import educationImg from "../../images/use-case/education-img.png";
import ngoImg from "../../images/use-case/ngo-img.png";
import mediaImg from "../../images/use-case/media-img.png";
import travelImg from "../../images/use-case/travel-img.png";
import hrImg from "../../images/use-case/hr-img.png";

const allIndustries = [
  {
    id: "health-care",
    title: "Health Care & Public Health",
    image: healthCareImg,
    imagePosition: "left",
    intro:
      "When language is unclear, people don't just get confused—they disengage. In healthcare, that can mean missed appointments, poor medication adherence, and low trust.",
    howUsed: [
      {
        label: "Multilingual patient intake",
        text: "Translate intake questions and symptoms into the patient's preferred language."
      },
      {
        label: "Discharge & aftercare clarity",
        text: "Provide translated care instructions and follow-up guidance."
      },
      {
        label: "Audio-first accessibility",
        text: "Turn critical instructions into natural audio using Text-to-Speech for patients who prefer listening."
      },
      {
        label: "Clinical support workflows",
        text: "Convert spoken notes to text (Speech-to-Text) for faster documentation and review."
      }
    ],
    commonWorkflows: [
      "Localize clinic websites, appointment pages, and patient FAQs.",
      "Translate vaccination, prevention, and community health materials.",
      "Offer audio reminders for medications, follow-ups, and care plans.",
      "Transcribe hotline calls for quality review and better support"
    ],
    outcomes: [
      "Higher completion of forms and follow-ups",
      "Better understanding of care instructions",
      "More trust in patient-facing communications"
    ]
  },
  {
    id: "government",
    title: "Government & Public Services",
    image: governmentImg,
    imagePosition: "right",
    intro:
      "Public services work best when people can understand them—quickly, clearly, and without guesswork. Language barriers lead to incomplete forms, repeated visits, and low confidence in public systems.",
    howUsed: [
      {
        label: "Forms & applications",
        text: "Translate key public forms, instructions, and eligibility requirements."
      },
      {
        label: "Citizen communications",
        text: "Localize service pages, FAQs, and office notices for multilingual communities."
      },
      {
        label: "Public announcements",
        text: "Generate clear audio announcements for kiosks, hotlines, or service centers."
      },
      {
        label: "Support center efficiency",
        text: "Transcribe citizen calls and messages so teams can respond faster and more accurately."
      }
    ],
    commonWorkflows: [
      "Translate online portals for IDs, permits, benefits, and registrations.",
      "Provide multilingual \"How to apply\" guides and checklists.",
      "Create audio versions of essential instructions for accessibility.",
      "Localize emergency advisories and community alerts."
    ],
    outcomes: [
      "Higher form completion rates",
      "Fewer repeat visits and support tickets",
      "Clearer understanding of services and requirements"
    ]
  },
  {
    id: "fintech",
    title: "Financial Services & Fintech",
    image: financeImg,
    imagePosition: "left",
    theme: "green",
    intro:
      "Trust is everything in finance. If customers don't fully understand a loan term, a verification step, or a policy message, they may abandon onboarding—or avoid using the service entirely.",
    howUsed: [
      {
        label: "Multilingual onboarding & KYC",
        text: "Translate onboarding screens, instructions, and verification prompts."
      },
      {
        label: "Clear product explainers",
        text: "Localize loan terms, savings products, and fee explanations in simple language."
      },
      {
        label: "Support and dispute resolution",
        text: "Transcribe and translate support interactions for faster resolution."
      },
      {
        label: "Accessible voice prompts",
        text: "Use Text-to-Speech for IVR menus or audio explainers inside the app."
      }
    ],
    commonWorkflows: [
      "Localize banking and fintech mobile UI (menus, prompts, notifications).",
      "Translate customer support knowledge base articles",
      "Create multilingual fraud-awareness and safety messages.",
      "Offer audio-first experiences for key steps (verification, confirmations, reminders)."
    ],
    outcomes: [
      "Increased onboarding completion",
      "Fewer misunderstandings and support escalations",
      "Stronger customer trust and retention"
    ]
  },
  {
    id: "telecom",
    title: "Telecom & Customer Support",
    image: telecomImg,
    imagePosition: "right",
    intro:
      "Support teams deal with fast-moving conversations where language friction slows everything down—handle time, accuracy, and customer satisfaction.",
    howUsed: [
      {
        label: "Call transcription",
        text: "Convert spoken conversations into text for QA, training, and documentation"
      },
      {
        label: "Multilingual support content",
        text: "Translate troubleshooting guides, FAQs, and self-service flows."
      },
      {
        label: "Localized IVR and prompts",
        text: "Use Text-to-Speech to offer natural audio in supported languages."
      },
      {
        label: "Agent workflows",
        text: "Help teams capture customer intent and key details even when code-switching occurs."
      }
    ],
    commonWorkflows: [
      "Transcribe calls for coaching, compliance, and dispute handling.",
      "Translate \"how-to\" support articles for different markets.",
      "Localize outage updates and service notices.",
      "Add multilingual voice prompts to reduce calls and improve self-service."
    ],
    outcomes: [
      "Faster resolutions and improved handle time",
      "Better self-service success",
      "Higher customer satisfaction"
    ]
  },
  {
    id: "education",
    title: "Education & EdTech",
    image: educationImg,
    imagePosition: "left",
    intro:
      "Learning improves when language doesn't get in the way. Students, parents, and educators need tools that make education accessible across languages—without losing meaning.",
    howUsed: [
      {
        label: "Localized learning content",
        text: "Translate lessons, quizzes, and study materials."
      },
      {
        label: "Pronunciation & listening support",
        text: "Use Text-to-Speech to generate audio for practice and reinforcement."
      },
      {
        label: "Speech-to-text for learning",
        text: "Convert student responses or teacher explanations into text for review."
      },
      {
        label: "Family engagement",
        text: "Translate school announcements, policies, and parent communications"
      }
    ],
    commonWorkflows: [
      "Localize course modules and classroom resources.",
      "Create audio companion content for reading practice",
      "Translate progress updates and learning instructions for parents/guardians",
      "Support multilingual classrooms with consistent language access"
    ],
    outcomes: [
      "Higher engagement and comprehension",
      "Better accessibility for students and families",
      "More inclusive learning experiences"
    ]
  },
  {
    id: "ngo",
    title: "NGOs & Humanitarian Services",
    image: ngoImg,
    imagePosition: "right",
    intro:
      "In humanitarian work, clarity saves time—and can save lives. Outreach, consent, training, and crisis communications must be understood by the communities they serve.",
    howUsed: [
      {
        label: "Community outreach",
        text: "Translate program information, resource guides, and community updates."
      },
      {
        label: "Surveys & field data",
        text: "Transcribe voice notes and translate responses for analysis."
      },
      {
        label: "Crisis communications",
        text: "Deliver multilingual advisories fast, with both text and audio formats"
      },
      {
        label: "Training materials",
        text: "Localize volunteer training and operational guides"
      }
    ],
    commonWorkflows: [
      "Translate webpages, flyers, and service directories.",
      "Create audio versions of critical messages for broader reach.",
      "Transcribe and translate field interviews and voice notes.",
      "Localize feedback forms and surveys for participation."
    ],
    outcomes: [
      "Higher reach and understanding",
      "Better community participation",
      "Faster response in high-need moments"
    ]
  },
  {
    id: "media",
    title: "Media, Content & Entertainment",
    image: mediaImg,
    imagePosition: "left",
    intro:
      "Creators need speed—but they also need meaning. Good localization isn't just word replacement; it preserves tone, humor, and cultural context.",
    howUsed: [
      {
        label: "Transcription for audio/video",
        text: "Turn speech into text for editing, search, and publishing."
      },
      {
        label: "Subtitles and captions",
        text: "Translate transcripts into additional languages for wider reach."
      },
      {
        label: "Audio localization",
        text: "Generate natural voice audio for explainers and short-form content."
      },
      {
        label: "Content workflows",
        text: "Localize descriptions, metadata, and community posts for regional audiences."
      }
    ],
    commonWorkflows: [
      "Transcribe podcasts, interviews, and video content",
      "Create multilingual captions for social media.",
      "Translate scripts, summaries, and educational content.",
      "Produce audio-first content versions for accessibility."
    ],
    outcomes: [
      "Faster production and localization",
      "Higher audience reach and watch-time",
      "More inclusive content experiences"
    ]
  },
  {
    id: "travel",
    title: "Travel, Hospitality & Transportation",
    image: travelImg,
    imagePosition: "right",
    theme: "green",
    intro:
      "Great experiences depend on clear instructions: check-in, safety, directions, policies, and support. Language barriers cause confusion—and friction at the worst times.",
    howUsed: [
      {
        label: "Localize journeys",
        text: "Translate booking flows, policies, and support instructions"
      },
      {
        label: "Guest support",
        text: "Provide multilingual FAQs and service communication."
      },
      {
        label: "Safety information",
        text: "Offer audio instructions and alerts in accessible formats."
      },
      {
        label: "On-the-ground content",
        text: "Translate signage, guides, and location information."
      }
    ],
    commonWorkflows: [
      "Localize confirmations, check-in instructions, and service notices",
      "Provide multilingual support for disruptions or changes.",
      "Add audio guides for tours, venues, or transport hubs.",
      "Translate customer service scripts and knowledge base."
    ],
    outcomes: [
      "Better guest satisfaction and fewer support issues",
      "Clearer safety and compliance messaging",
      "Smoother travel experiences across markets"
    ]
  },
  {
    id: "hr",
    title: "HR, Workforce & Training",
    image: hrImg,
    imagePosition: "left",
    intro:
      "Multilingual teams perform better when onboarding, safety instructions, and internal communication are clearly understood—especially for frontline and distributed workforces",
    howUsed: [
      {
        label: "Onboarding localization",
        text: "Translate onboarding flows, policies, and HR communications."
      },
      {
        label: "Safety and compliance training",
        text: "Localize procedures and required training materials."
      },
      {
        label: "Internal knowledge sharing",
        text: "Transcribe meetings or voice updates into searchable text."
      },
      {
        label: "Audio guidance",
        text: "Provide Text-to-Speech versions of critical instructions for accessibility."
      }
    ],
    commonWorkflows: [
      "Translate SOPs, handbooks, and workplace policies.",
      "Localize training modules for different regions and roles",
      "Transcribe shift handovers and operational briefings.",
      "Create multilingual \"quick guides\" for common workplace tasks."
    ],
    outcomes: [
      "Fewer misunderstandings and workplace incidents",
      "Faster onboarding and time-to-productivity",
      "More consistent process adoption across sites"
    ]
  }
];

const IndustryDetailSection = () => {
  return (
    <div className="w-full flex flex-col">
      {allIndustries.map((ind) => {
        const isImageRight = ind.imagePosition === "right";
        const isGreen = ind.theme === "green";

        return (
          <section
            key={ind.id}
            id={ind.id}
            className={`w-full py-14 sm:py-20 scroll-mt-24 ${isGreen
              ? "bg-[#1E6551] text-white"
              : "bg-white text-gray-900 border-b border-gray-100 last:border-b-0"
              }`}
          >
            <div className="max-w-8xl mx-auto px-8 sm:px-12 md:px-16 lg:px-24 xl:px-32">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                {/* Image Column */}
                <div
                  className={`lg:col-span-5 flex justify-center ${isImageRight ? "lg:order-2" : "lg:order-1"
                    }`}
                  data-aos={isImageRight ? "fade-left" : "fade-right"}
                >
                  <div
                    className={`w-full max-w-[480px] lg:max-w-none overflow-hidden ${isGreen
                      ? "rounded-[24px] sm:rounded-[32px] shadow-2xl"
                      : "rounded-[28px] sm:rounded-[36px] shadow-lg border border-[#E2EAE5] bg-[#F2F7F4]"
                      }`}
                  >
                    <img
                      src={ind.image}
                      alt={ind.title}
                      className="w-full h-auto object-cover object-top max-h-[640px] sm:max-h-[720px] lg:max-h-[820px]"
                    />
                  </div>
                </div>

                {/* Text Content Column */}
                <div
                  className={`lg:col-span-7 flex flex-col justify-center text-left ${isImageRight ? "lg:order-1" : "lg:order-2"
                    }`}
                  data-aos={isImageRight ? "fade-right" : "fade-left"}
                >
                  {/* Title */}
                  <h2
                    className={`text-3xl sm:text-4xl lg:text-[44px] font-bold font-['Axiforma',sans-serif] tracking-tight leading-tight mb-5 ${isGreen ? "text-white" : "text-[#0D3429]"
                      }`}
                  >
                    {ind.title}
                  </h2>

                  {/* Intro paragraph */}
                  <p
                    className={`text-sm sm:text-base leading-relaxed mb-8 font-normal ${isGreen ? "text-emerald-50/90" : "text-gray-600"
                      }`}
                  >
                    {ind.intro}
                  </p>

                  {/* Sub-section 1: How NKENNEAi Can Be Used */}
                  <div className="mb-7">
                    <h3
                      className={`text-base sm:text-lg font-bold font-['Axiforma',sans-serif] mb-3 ${isGreen ? "text-white" : "text-[#0D3429]"
                        }`}
                    >
                      How NKENNEAi Can Be Used
                    </h3>
                    <ul className="space-y-2.5">
                      {ind.howUsed.map((item, idx) => (
                        <li
                          key={idx}
                          className={`text-xs sm:text-sm leading-relaxed flex items-start ${isGreen ? "text-emerald-50/90" : "text-gray-700"
                            }`}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full mt-2 mr-2.5 shrink-0 ${isGreen ? "bg-white" : "bg-gray-600"
                              }`}
                          ></span>
                          <span>
                            <strong
                              className={`font-semibold ${isGreen ? "text-white" : "text-gray-900"
                                }`}
                            >
                              {item.label}:
                            </strong>{" "}
                            {item.text}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Sub-section 2: Common Work Flows */}
                  <div className="mb-7">
                    <h3
                      className={`text-base sm:text-lg font-bold font-['Axiforma',sans-serif] mb-3 ${isGreen ? "text-white" : "text-[#0D3429]"
                        }`}
                    >
                      Common Work Flows
                    </h3>
                    <ul className="space-y-2.5">
                      {ind.commonWorkflows.map((workflow, idx) => (
                        <li
                          key={idx}
                          className={`text-xs sm:text-sm leading-relaxed flex items-start ${isGreen ? "text-emerald-50/90" : "text-gray-700"
                            }`}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full mt-2 mr-2.5 shrink-0 ${isGreen ? "bg-white" : "bg-gray-600"
                              }`}
                          ></span>
                          <span>{workflow}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Sub-section 3: Outcomes Teams Aim For */}
                  <div className="mb-4">
                    <h3
                      className={`text-base sm:text-lg font-bold font-['Axiforma',sans-serif] mb-3 ${isGreen ? "text-white" : "text-[#0D3429]"
                        }`}
                    >
                      Outcomes Teams Aim For
                    </h3>
                    <ul className="space-y-2.5">
                      {ind.outcomes.map((outcome, idx) => (
                        <li
                          key={idx}
                          className={`text-xs sm:text-sm leading-relaxed flex items-start ${isGreen ? "text-emerald-50/90" : "text-gray-700"
                            }`}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full mt-2 mr-2.5 shrink-0 ${isGreen ? "bg-white" : "bg-gray-600"
                              }`}
                          ></span>
                          <span>{outcome}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>
        );
      })}
    </div>
  );
};

export default IndustryDetailSection;
